import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, FolderPlus, Folder, Pencil, Trash2, GitMerge, Lock, ListTree, Plus, X, CornerDownRight, MoveRight, FileText, Search } from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader } from '../../components/PageHeader';
import { portfolioService, Portfolio, TaxonomyNode, TAXONOMY_LEVEL_LABELS } from '../../services/portfolio.service';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';

/**
 * NQG-RQ-29 — Portfolio management. Portfolios group PDFs (and, via
 * inheritance, their generated questions) by client/origin. Create, rename,
 * merge and delete; deletion migrates PDFs and questions to the default
 * portfolio.
 *
 * NQG-RQ-31 — each portfolio additionally owns a 3-level taxonomy tree
 * (Theme → Subtheme → Sub-subtheme), managed from the card's Taxonomy button.
 */

export function PortfoliosManagement() {
  const queryClient = useQueryClient();
  const [taxonomyFor, setTaxonomyFor] = useState<Portfolio | null>(null); // NQG-RQ-31
  const [detailPortfolio, setDetailPortfolio] = useState<Portfolio | null>(null); // portfolio detail (its PDFs)

  const { data: portfolios = [], isLoading, error } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    queryClient.invalidateQueries({ queryKey: ['portfolio-pdfs'] });
  };

  const showError = (err: any) =>
    Swal.fire({
      title: 'Error',
      text: err.response?.data?.error || 'Unexpected error',
      icon: 'error',
      confirmButtonColor: '#dc2626',
    });

  const handleCreate = async () => {
    const { value } = await Swal.fire({
      title: 'New portfolio',
      html: `
        <input id="pfName" class="swal2-input" placeholder="Portfolio name (e.g. PsiPartners)">
        <input id="pfDesc" class="swal2-input" placeholder="Description (optional)">
      `,
      showCancelButton: true,
      confirmButtonText: 'Create',
      confirmButtonColor: '#1e73be',
      preConfirm: () => {
        const name = (document.getElementById('pfName') as HTMLInputElement)?.value.trim();
        const description = (document.getElementById('pfDesc') as HTMLInputElement)?.value.trim();
        if (!name) {
          Swal.showValidationMessage('Portfolio name is required');
          return false;
        }
        return { name, description };
      },
    });
    if (!value) return;
    try {
      await portfolioService.create(value.name, value.description);
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleRename = async (p: Portfolio) => {
    const { value } = await Swal.fire({
      title: 'Rename portfolio',
      input: 'text',
      inputValue: p.name,
      showCancelButton: true,
      confirmButtonText: 'Rename',
      confirmButtonColor: '#1e73be',
      inputValidator: (v) => (!v.trim() ? 'Name is required' : undefined),
    });
    if (!value) return;
    try {
      await portfolioService.rename(p._id, value.trim());
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleMerge = async (p: Portfolio) => {
    const targets = portfolios.filter((o) => o._id !== p._id);
    if (targets.length === 0) return;
    const options = targets.map((t) => `<option value="${t._id}">${t.name}</option>`).join('');
    const { value } = await Swal.fire({
      title: `Merge "${p.name}" into…`,
      html: `<p style="font-size:13px;color:#555;margin-bottom:8px">Its PDFs and questions will be re-tagged to the target, then "${p.name}" is removed.</p>
             <select id="pfTarget" class="swal2-select" style="width:80%">${options}</select>`,
      showCancelButton: true,
      confirmButtonText: 'Merge',
      confirmButtonColor: '#1e73be',
      preConfirm: () => (document.getElementById('pfTarget') as HTMLSelectElement)?.value,
    });
    if (!value) return;
    try {
      await portfolioService.merge(p._id, value);
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleDelete = async (p: Portfolio) => {
    const confirm = await Swal.fire({
      title: `Delete "${p.name}"?`,
      html: `<p style="font-size:14px;color:#555">Its PDFs and generated questions will migrate to the default portfolio. This cannot be undone.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;
    try {
      await portfolioService.remove(p._id);
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Portfolios"
          description="Group PDFs and their generated questions by client or origin"
          breadcrumbs={[{ label: 'Booklet Analysis' }, { label: 'Portfolios' }]}
        />
        <button
          onClick={handleCreate}
          className="px-6 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
        >
          <FolderPlus className="w-5 h-5" /> New Portfolio
        </button>
      </div>

      {error ? (
        <div className="p-10 text-center text-sm text-red-500">
          {(error as any)?.response?.data?.error || 'Failed to load portfolios.'}
        </div>
      ) : isLoading ? (
        <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Loading...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {portfolios.map((p) => (
            <div key={p._id} className="bg-white rounded-2xl shadow-md border border-gray-100 p-5">
              <div className="flex items-start justify-between">
                <button
                  onClick={() => setDetailPortfolio(p)}
                  className="flex items-center gap-3 text-left group"
                  title="View this portfolio's PDFs"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${p.isDefault ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-[#1e73be]'}`}>
                    <Folder className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 flex items-center gap-1.5 group-hover:text-[#1e73be]">
                      {p.name}
                      {p.isDefault && <Lock className="w-3.5 h-3.5 text-gray-400" />}
                    </p>
                    <p className="text-xs text-gray-500 group-hover:text-[#1e73be]">{p.pdfCount} PDF(s) · view</p>
                  </div>
                </button>
              </div>
              {p.description && <p className="text-xs text-gray-500 mt-3">{p.description}</p>}

              <div className="flex items-center gap-1 mt-4 pt-3 border-t border-gray-100">
                <button onClick={() => setTaxonomyFor(p)} className="p-2 rounded-lg text-gray-500 hover:text-purple-600 hover:bg-purple-50 flex items-center gap-1.5 text-xs font-semibold" title="Manage taxonomy (Theme → Subtheme → Sub-subtheme)">
                  <ListTree className="w-4 h-4" /> Taxonomy
                </button>
                {!p.isDefault && (
                  <>
                    <button onClick={() => handleRename(p)} className="p-2 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50" title="Rename">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleMerge(p)} className="p-2 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50" title="Merge into another portfolio">
                      <GitMerge className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(p)} className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50" title="Delete (migrates to default)">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              {p.isDefault && (
                <p className="text-[11px] text-gray-400 mt-2">
                  Default portfolio — PDFs without an explicit assignment live here.
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {taxonomyFor && (
        <TaxonomyManagerModal portfolio={taxonomyFor} onClose={() => setTaxonomyFor(null)} />
      )}

      {detailPortfolio && (
        <PortfolioDetailModal
          portfolio={detailPortfolio}
          onClose={() => setDetailPortfolio(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );
}

// ── Portfolio detail: list its PDFs + bulk-add more (client feedback) ────────

function PortfolioDetailModal({
  portfolio,
  onClose,
  onChanged,
}: {
  portfolio: Portfolio;
  onClose: () => void;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['portfolio-pdfs', portfolio._id],
    queryFn: () => portfolioService.pdfsInPortfolio(portfolio._id),
  });

  // All analyses (to pick from when adding)
  const { data: allPdfs = [] } = useQuery({
    queryKey: ['all-analyses-for-portfolio'],
    queryFn: () => pdfAnalysisService.getAnalyses(),
    enabled: addOpen,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['portfolio-pdfs', portfolio._id] });
    onChanged();
  };

  const memberIds = new Set((data?.pdfs || []).map((p: any) => p._id));
  const candidates = (allPdfs as any[])
    .filter((p) => !memberIds.has(p._id))
    .filter((p) => p.filename.toLowerCase().includes(search.toLowerCase()));

  const handleAdd = async () => {
    if (selected.size === 0) return;
    try {
      await portfolioService.addPdfsToPortfolio(portfolio._id, Array.from(selected));
      setSelected(new Set());
      setAddOpen(false);
      refresh();
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.response?.data?.error || 'Could not add PDFs.', icon: 'error', confirmButtonColor: '#dc2626' });
    }
  };

  const handleRemove = async (pdfId: string) => {
    try {
      await portfolioService.removePdfFromPortfolio(portfolio._id, pdfId);
      refresh();
    } catch (err: any) {
      Swal.fire({ title: 'Error', text: err.response?.data?.error || 'Could not remove PDF.', icon: 'error', confirmButtonColor: '#dc2626' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Folder className="w-5 h-5 text-[#1e73be]" />
            <h2 className="text-lg font-bold text-gray-800">{portfolio.name}</h2>
            <span className="text-xs text-gray-400">{data?.pdfs?.length ?? 0} PDF(s)</span>
          </div>
          <div className="flex items-center gap-2">
            {!portfolio.isDefault && (
              <button
                onClick={() => setAddOpen((v) => !v)}
                className="px-3 py-1.5 rounded-lg bg-[#1e73be] text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-[#1e73be]/90"
              >
                <Plus className="w-3.5 h-3.5" /> Add PDFs
              </button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Bulk-add panel */}
        {addOpen && (
          <div className="mb-4 rounded-xl border border-gray-200 p-3 bg-gray-50">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search PDFs to add..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
              />
            </div>
            <div className="max-h-52 overflow-y-auto space-y-1">
              {candidates.length === 0 ? (
                <p className="text-xs text-gray-400 p-2">No other PDFs to add.</p>
              ) : (
                candidates.map((p) => (
                  <label key={p._id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={selected.has(p._id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(p._id) : next.delete(p._id);
                        setSelected(next);
                      }}
                    />
                    <FileText className="w-3.5 h-3.5 text-gray-400" />
                    <span className="truncate">{p.filename}</span>
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-end mt-2">
              <button
                onClick={handleAdd}
                disabled={selected.size === 0}
                className="px-4 py-1.5 rounded-lg bg-[#1e73be] text-white text-xs font-semibold disabled:opacity-40"
              >
                Add {selected.size > 0 ? `${selected.size} ` : ''}selected
              </button>
            </div>
          </div>
        )}

        {/* Member PDFs */}
        {isLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Loading PDFs...</span>
          </div>
        ) : (data?.pdfs?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400 p-6 text-center">No PDFs in this portfolio yet.</p>
        ) : (
          <div className="space-y-1.5">
            {data!.pdfs.map((p: any) => (
              <div key={p._id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">{p.filename}</span>
                  <span className="text-[10px] text-gray-400">{p.totalItems} topic(s)</span>
                </div>
                {!portfolio.isDefault && (
                  <button
                    onClick={() => handleRemove(p._id)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove from this portfolio"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── NQG-RQ-31: taxonomy tree manager ────────────────────────────────────────

function TaxonomyManagerModal({ portfolio, onClose }: { portfolio: Portfolio; onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data: nodes = [], isLoading } = useQuery({
    queryKey: ['taxonomy', portfolio._id],
    queryFn: () => portfolioService.getTaxonomy(portfolio._id),
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['taxonomy', portfolio._id] });

  const showError = (err: any) =>
    Swal.fire({
      title: 'Error',
      text: err.response?.data?.error || 'Unexpected error',
      icon: 'error',
      confirmButtonColor: '#dc2626',
    });

  const byParent = useMemo(() => {
    const map = new Map<string, TaxonomyNode[]>();
    for (const n of nodes) {
      const key = n.parentId ? String(n.parentId) : 'root';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(n);
    }
    map.forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [nodes]);

  const byId = useMemo(() => new Map(nodes.map((n) => [String(n._id), n])), [nodes]);

  const descendantIdsOf = (nodeId: string): Set<string> => {
    const result = new Set<string>();
    const walk = (id: string) => {
      for (const child of byParent.get(id) || []) {
        result.add(String(child._id));
        walk(String(child._id));
      }
    };
    walk(nodeId);
    return result;
  };

  const subtreeHeightOf = (nodeId: string): number => {
    const children = byParent.get(nodeId) || [];
    if (children.length === 0) return 1;
    return 1 + Math.max(...children.map((c) => subtreeHeightOf(String(c._id))));
  };

  const handleAdd = async (parent: TaxonomyNode | null) => {
    const level = parent ? parent.level + 1 : 1;
    const { value } = await Swal.fire({
      title: parent ? `New ${TAXONOMY_LEVEL_LABELS[level]} under "${parent.name}"` : `New ${TAXONOMY_LEVEL_LABELS[1]}`,
      input: 'text',
      inputPlaceholder: `${TAXONOMY_LEVEL_LABELS[level]} name`,
      showCancelButton: true,
      confirmButtonText: 'Create',
      confirmButtonColor: '#1e73be',
      inputValidator: (v) => (!v.trim() ? 'Name is required' : undefined),
    });
    if (!value) return;
    try {
      await portfolioService.createNode(portfolio._id, value.trim(), parent ? String(parent._id) : null);
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleRenameNode = async (node: TaxonomyNode) => {
    const { value } = await Swal.fire({
      title: `Rename "${node.name}"`,
      input: 'text',
      inputValue: node.name,
      showCancelButton: true,
      confirmButtonText: 'Rename',
      confirmButtonColor: '#1e73be',
      inputValidator: (v) => (!v.trim() ? 'Name is required' : undefined),
    });
    if (!value) return;
    try {
      await portfolioService.renameNode(String(node._id), value.trim());
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleMoveNode = async (node: TaxonomyNode) => {
    const blocked = descendantIdsOf(String(node._id));
    blocked.add(String(node._id));
    const height = subtreeHeightOf(String(node._id));
    // Valid parents: any node whose level + subtree height fits within 3
    const candidates = nodes.filter(
      (n) => !blocked.has(String(n._id)) && n.level + height <= 3 && String(n._id) !== String(node.parentId)
    );
    const options = [
      ...(node.parentId && 1 + height - 1 <= 3 ? [`<option value="">Top level (${TAXONOMY_LEVEL_LABELS[1]})</option>`] : []),
      ...candidates.map((n) => {
        const path: string[] = [];
        let cur: TaxonomyNode | undefined = n;
        while (cur) {
          path.unshift(cur.name);
          cur = cur.parentId ? byId.get(String(cur.parentId)) : undefined;
        }
        return `<option value="${n._id}">${path.join(' › ')}</option>`;
      }),
    ].join('');
    if (!options) {
      Swal.fire({ title: 'No valid destination', text: 'There is nowhere this node can move without exceeding 3 levels.', icon: 'info', confirmButtonColor: '#1e73be' });
      return;
    }
    const result = await Swal.fire({
      title: `Move "${node.name}" under…`,
      html: `<select id="taxMoveTarget" class="swal2-select" style="width:85%">${options}</select>`,
      showCancelButton: true,
      confirmButtonText: 'Move',
      confirmButtonColor: '#1e73be',
      preConfirm: () => (document.getElementById('taxMoveTarget') as HTMLSelectElement)?.value ?? '',
    });
    if (!result.isConfirmed) return;
    try {
      await portfolioService.moveNode(String(node._id), result.value || null);
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const handleDeleteNode = async (node: TaxonomyNode) => {
    const descendants = descendantIdsOf(String(node._id)).size;
    const confirm = await Swal.fire({
      title: `Delete "${node.name}"?`,
      html: `<p style="font-size:13px;color:#555">${
        descendants > 0 ? `Its ${descendants} sub-node(s) are deleted too. ` : ''
      }PDFs placed here lose this taxonomy path (they keep their portfolio tag). Already-generated questions keep the path stamped on them.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;
    try {
      await portfolioService.deleteNode(String(node._id));
      invalidate();
    } catch (err) {
      showError(err);
    }
  };

  const renderNodes = (parentKey: string, depth: number) => {
    const children = byParent.get(parentKey) || [];
    return children.map((node) => (
      <div key={String(node._id)}>
        <div
          className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 group"
          style={{ marginLeft: depth * 24 }}
        >
          {depth > 0 && <CornerDownRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />}
          <span
            className={`text-xs font-bold px-2 py-0.5 rounded flex-shrink-0 ${
              node.level === 1
                ? 'bg-purple-100 text-purple-700'
                : node.level === 2
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-emerald-100 text-emerald-700'
            }`}
          >
            L{node.level}
          </span>
          <span className="text-sm font-medium text-gray-800 flex-1 truncate">{node.name}</span>
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {node.level < 3 && (
              <button onClick={() => handleAdd(node)} className="p-1.5 rounded text-gray-400 hover:text-green-600 hover:bg-green-50" title={`Add ${TAXONOMY_LEVEL_LABELS[node.level + 1]}`}>
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={() => handleRenameNode(node)} className="p-1.5 rounded text-gray-400 hover:text-[#1e73be] hover:bg-blue-50" title="Rename">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleMoveNode(node)} className="p-1.5 rounded text-gray-400 hover:text-amber-600 hover:bg-amber-50" title="Move">
              <MoveRight className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => handleDeleteNode(node)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" title="Delete (with sub-nodes)">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {renderNodes(String(node._id), depth + 1)}
      </div>
    ));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
              <ListTree className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Taxonomy — {portfolio.name}</p>
              <p className="text-xs text-gray-500">Theme → Subtheme → Sub-subtheme (3 levels max)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">Loading…</span>
            </div>
          ) : nodes.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-400">
              No taxonomy yet — add the first {TAXONOMY_LEVEL_LABELS[1]} below.
            </p>
          ) : (
            renderNodes('root', 0)
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={() => handleAdd(null)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] text-white rounded-xl text-sm font-semibold shadow hover:shadow-lg transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add {TAXONOMY_LEVEL_LABELS[1]}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PortfoliosManagement;
